import styled from '@emotion/styled';
import { Diversity3Rounded, PersonRounded } from '@mui/icons-material';
import type { Dispatch, SetStateAction } from 'react';
import type { AuthDialogMode } from '~/modules/Auth/KaraokeAuthModal';
import type { AppliedFilters } from '~/routes/SingASong/SongSelection/Hooks/useSongListFilter';

interface Props {
  songCount: number;
  filters: AppliedFilters;
  setFilters: Dispatch<SetStateAction<AppliedFilters>>;
  onOpenAuth?: (mode: AuthDialogMode) => void;
}

export default function KaraokeCatalogToolbar({ songCount, filters, setFilters }: Props) {
  const modeFilter = filters.duet ?? null;

  const setModeFilter = (duet: boolean) => {
    setFilters((current) => ({
      ...current,
      duet: current.duet === duet ? null : duet,
    }));
  };

  return (
    <Toolbar aria-label="Karaoke catalog actions">
      <SongCount>{songCount} songs</SongCount>

      <ModeFilters aria-label="Filtrar canciones por modo">
        <ModeFilterButton
          type="button"
          aria-pressed={modeFilter === false}
          data-active={modeFilter === false}
          onClick={() => setModeFilter(false)}>
          <PersonRounded sx={{ fontSize: 18 }} />
          <span>Single mode</span>
        </ModeFilterButton>

        <ModeFilterButton
          type="button"
          aria-pressed={modeFilter === true}
          data-active={modeFilter === true}
          onClick={() => setModeFilter(true)}>
          <Diversity3Rounded sx={{ fontSize: 18 }} />
          <span>Twin mode</span>
        </ModeFilterButton>
      </ModeFilters>
    </Toolbar>
  );
}

const Toolbar = styled.header`
  position: absolute;
  top: 0;
  left: var(--song-sidebar-weight);
  width: calc(100% - var(--song-sidebar-weight));
  height: var(--song-catalog-toolbar-height, 44px);
  z-index: 110;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 0 16px;
  border-bottom: 1px solid var(--walky-border);
  background: var(--walky-surface-translucent);
  color: var(--walky-ink);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(14px);

  @media (max-width: 640px) {
    gap: 8px;
    padding: 0 8px;
  }
`;

const SongCount = styled.div`
  color: var(--walky-muted);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;

  @media (max-width: 640px) {
    font-size: 12px;
  }
`;

const ModeFilters = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border: 1px solid var(--walky-border);
  border-radius: 8px;
  background: var(--walky-canvas);

  @media (max-width: 640px) {
    flex: 1 1 auto;
    min-width: 0;
    justify-content: flex-end;
  }
`;

const ModeFilterButton = styled.button`
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: var(--walky-ink);
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  transition: all 160ms ease;

  &:hover,
  &:focus-visible {
    color: var(--walky-orange);
    background: var(--walky-surface-raised);
    border-color: rgba(244, 90, 10, 0.28);
    outline: none;
  }

  &[data-active='true'] {
    color: #ffffff;
    background: linear-gradient(135deg, var(--walky-orange), #ff7a1a);
    border-color: rgba(244, 90, 10, 0.52);
    box-shadow: 0 4px 12px rgba(244, 90, 10, 0.18);
  }

  @media (max-width: 640px) {
    flex: 1 1 0;
    min-width: 0;
    min-height: 32px;
    padding: 0 6px;
    font-size: 11px;
    gap: 4px;
  }
`;
